import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Plus, Pencil, Trash, FloppyDisk, X, UploadSimple, DownloadSimple,
  MagnifyingGlass, ArrowsClockwise, FileCsv, CheckCircle, XCircle, ArrowCounterClockwise,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { API_BASE, formatApiErrorDetail } from "@/lib/api";

const EMPTY_FORM = {
  student_no: "",
  full_name: "",
  nationality: "",
  date_of_birth: "",
  email: "",
  phone: "",
  notes: "",
};

export default function Students() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [includeInactive, setIncludeInactive] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/students", { params: { q: q || undefined, include_inactive: includeInactive } });
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Öğrenciler yüklenemedi.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [includeInactive]);

  const startNew = () => setEditing({ ...EMPTY_FORM });
  const startEdit = (it) => setEditing({
    student_no: it.student_no,
    full_name: it.full_name,
    nationality: it.nationality,
    date_of_birth: it.date_of_birth,
    email: it.email || "",
    phone: it.phone || "",
    notes: it.notes || "",
    _editing: true,
  });
  const cancel = () => setEditing(null);

  const save = async () => {
    if (!editing.full_name?.trim() || editing.full_name.trim().length < 2) {
      toast.error("Ad-Soyad zorunlu (en az 2 karakter).");
      return;
    }
    if (!editing.nationality?.trim()) {
      toast.error("Uyruk zorunlu.");
      return;
    }
    if (!editing.date_of_birth) {
      toast.error("Doğum tarihi zorunlu.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        full_name: editing.full_name.trim(),
        nationality: editing.nationality.trim(),
        date_of_birth: editing.date_of_birth,
        email: editing.email?.trim() || null,
        phone: editing.phone?.trim() || null,
        notes: editing.notes?.trim() || null,
      };
      if (editing._editing) {
        await api.put(`/students/${encodeURIComponent(editing.student_no)}`, payload);
        toast.success("Öğrenci güncellendi.");
      } else {
        if (editing.student_no?.trim()) payload.student_no = editing.student_no.trim();
        const { data } = await api.post("/students", payload);
        toast.success(`Öğrenci kaydedildi: ${data.student_no}`);
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Kaydedilemedi.");
    } finally {
      setSaving(false);
    }
  };

  const softDelete = async (it) => {
    if (!window.confirm(`"${it.full_name}" pasifleştirilsin mi?\n(Sertifikaları korunur, listeden gizlenir.)`)) return;
    try {
      await api.delete(`/students/${encodeURIComponent(it.student_no)}`);
      toast.success("Öğrenci pasifleştirildi.");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "İşlem başarısız.");
    }
  };

  const restore = async (it) => {
    try {
      await api.post(`/students/${encodeURIComponent(it.student_no)}/restore`);
      toast.success("Öğrenci aktif yapıldı.");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "İşlem başarısız.");
    }
  };

  const onBulkUpload = async (e) => {
    e.preventDefault();
    if (!bulkFile) { toast.error("Önce bir dosya seçin."); return; }
    setBulkSubmitting(true);
    setBulkResult(null);
    try {
      const form = new FormData();
      form.append("file", bulkFile);
      const { data } = await api.post("/students/bulk-upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setBulkResult(data);
      toast.success(`${data.created_count} öğrenci eklendi, ${data.error_count} hata.`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Yükleme başarısız.");
    } finally {
      setBulkSubmitting(false);
    }
  };

  const filtered = items;

  return (
    <div className="p-6 lg:p-10" data-testid="students-page">
      <div className="flex items-end justify-between gap-6 flex-wrap">
        <div>
          <span className="label-eyebrow">/ Öğrenciler</span>
          <h1 className="font-display text-4xl lg:text-5xl font-black tracking-tighter text-slate-900 mt-3">
            Öğrenci kaydı
          </h1>
          <p className="text-slate-600 mt-2 max-w-2xl">
            Önce burada öğrenci oluşturun. Sonra <Link to="/admin/issue" className="underline text-[#0B2046]">Sertifika Oluştur</Link> veya{" "}
            <Link to="/admin/bulk-ops" className="underline text-[#0B2046]">Toplu İşlem</Link> sayfasından öğrencilere sertifika atayın.
            Öğrenci numarası boş bırakılırsa otomatik üretilir (IAVA-STD-{new Date().getFullYear()}-XXXX).
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setBulkOpen((v) => !v)}
            data-testid="students-bulk-toggle"
            className="inline-flex items-center gap-2 px-4 py-3 border border-slate-300 hover:bg-slate-50 text-sm font-medium"
          >
            <UploadSimple size={16} /> Excel/CSV ile Yükle
          </button>
          <button
            onClick={startNew}
            data-testid="student-add-button"
            className="inline-flex items-center gap-2 px-5 py-3 bg-[#0B2046] text-white hover:bg-[#113066] font-medium"
          >
            <Plus size={16} weight="bold" /> Yeni Öğrenci
          </button>
        </div>
      </div>

      {bulkOpen && (
        <div className="mt-6 grid lg:grid-cols-2 gap-4 max-w-5xl bg-slate-50 border border-slate-200 p-5">
          <div>
            <div className="label-eyebrow">Adım 1 — Şablon</div>
            <p className="text-sm text-slate-600 mt-2">
              Şablonu indirin, doldurun, sonra yükleyin. Boş <code className="bg-white px-1">student_no</code> sütunu otomatik atanır.
            </p>
            <a
              data-testid="students-template-download"
              href={`${API_BASE}/students/template.csv`}
              className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-slate-900 text-white text-sm font-medium hover:bg-slate-800"
            >
              <DownloadSimple size={14} /> Öğrenci CSV Şablonu
            </a>
            <div className="mt-3 text-xs text-slate-500">
              Sütunlar: <code className="bg-white px-1">student_no, full_name, nationality, date_of_birth, email, phone, notes</code>
            </div>
          </div>
          <form onSubmit={onBulkUpload} className="space-y-3">
            <div className="label-eyebrow">Adım 2 — Yükle</div>
            <label
              htmlFor="students-file"
              className="flex flex-col items-center justify-center border-2 border-dashed border-slate-300 hover:border-[#0B2046] cursor-pointer p-6 bg-white"
            >
              {bulkFile ? (
                <>
                  <FileCsv size={28} className="text-[#0B2046]" />
                  <div className="font-mono-plex text-sm mt-2">{bulkFile.name}</div>
                  <div className="text-xs text-slate-500">{(bulkFile.size / 1024).toFixed(1)} KB</div>
                </>
              ) : (
                <>
                  <UploadSimple size={28} className="text-slate-400" />
                  <div className="text-sm text-slate-700 mt-2">CSV / XLSX seç</div>
                </>
              )}
              <input
                id="students-file"
                type="file"
                accept=".csv,.xlsx"
                className="hidden"
                onChange={(e) => setBulkFile(e.target.files[0] || null)}
                data-testid="students-bulk-file-input"
              />
            </label>
            <button
              type="submit"
              disabled={bulkSubmitting || !bulkFile}
              data-testid="students-bulk-submit"
              className="w-full px-4 py-2.5 bg-[#0B2046] text-white text-sm font-medium hover:bg-[#113066] disabled:opacity-50"
            >
              {bulkSubmitting ? "Yükleniyor…" : "Öğrencileri Yükle"}
            </button>
            {bulkResult && (
              <div className="text-sm border bg-white border-slate-200 p-3 max-h-48 overflow-y-auto">
                <div className="flex gap-4 mb-2">
                  <span className="text-emerald-700">✓ Eklendi: <b>{bulkResult.created_count}</b></span>
                  <span className="text-red-700">✕ Hata: <b>{bulkResult.error_count}</b></span>
                </div>
                {bulkResult.errors?.length > 0 && (
                  <ul className="text-xs text-red-800 space-y-1">
                    {bulkResult.errors.map((er, i) => (
                      <li key={i}>Satır {er.row}: {er.error}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </form>
        </div>
      )}

      {editing && (
        <div className="mt-6 max-w-3xl bg-white border border-slate-200 p-6">
          <div className="label-eyebrow mb-4">{editing._editing ? `Düzenle — ${editing.student_no}` : "Yeni Öğrenci"}</div>
          <div className="grid md:grid-cols-2 gap-4">
            {!editing._editing && (
              <Input label="Öğrenci № (opsiyonel — boş ise otomatik)"
                value={editing.student_no}
                onChange={(e) => setEditing({ ...editing, student_no: e.target.value })}
                placeholder="IAVA-STD-2026-0001 (boş bırakılabilir)"
                testid="student-form-no" className="md:col-span-2" />
            )}
            <Input label="Ad Soyad *" required value={editing.full_name}
              onChange={(e) => setEditing({ ...editing, full_name: e.target.value })}
              testid="student-form-name" placeholder="Resmi kimlikte göründüğü gibi" />
            <Input label="Uyruk *" required value={editing.nationality}
              onChange={(e) => setEditing({ ...editing, nationality: e.target.value })}
              testid="student-form-nationality" placeholder="örn. Türk" />
            <Input label="Doğum tarihi *" type="date" required value={editing.date_of_birth}
              onChange={(e) => setEditing({ ...editing, date_of_birth: e.target.value })}
              testid="student-form-dob" />
            <Input label="E-posta" type="email" value={editing.email}
              onChange={(e) => setEditing({ ...editing, email: e.target.value })}
              testid="student-form-email" placeholder="opsiyonel" />
            <Input label="Telefon" value={editing.phone}
              onChange={(e) => setEditing({ ...editing, phone: e.target.value })}
              testid="student-form-phone" placeholder="opsiyonel" className="md:col-span-2" />
            <div className="md:col-span-2">
              <label className="label-eyebrow block mb-2">Notlar</label>
              <textarea
                value={editing.notes}
                onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                rows={2}
                data-testid="student-form-notes"
                className="w-full px-4 py-3 border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0B2046]"
              />
            </div>
          </div>
          <div className="mt-5 flex items-center justify-end gap-2">
            <button onClick={cancel} className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 inline-flex items-center gap-2">
              <X size={14} /> Vazgeç
            </button>
            <button
              onClick={save} disabled={saving}
              data-testid="student-form-save"
              className="px-5 py-2 bg-[#0B2046] text-white hover:bg-[#113066] font-medium text-sm inline-flex items-center gap-2 disabled:opacity-60"
            >
              <FloppyDisk size={14} weight="bold" /> {saving ? "Kaydediliyor…" : "Kaydet"}
            </button>
          </div>
        </div>
      )}

      <div className="mt-8 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[260px] max-w-xl">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="students-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Öğrenci № / ad / uyruk / e-posta…"
            className="w-full pl-10 pr-4 py-2.5 border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0B2046]"
          />
        </div>
        <button onClick={load} className="px-3 py-2 border border-slate-300 text-slate-700 hover:bg-slate-50 inline-flex items-center gap-2 text-sm">
          <ArrowsClockwise size={14} /> Yenile
        </button>
        <label className="inline-flex items-center gap-2 text-sm text-slate-700 ml-2">
          <input type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} data-testid="include-inactive" />
          Pasif öğrencileri de göster
        </label>
      </div>

      <div className="mt-4 border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <th className="px-4 py-3 text-left label-eyebrow">Öğrenci №</th>
              <th className="px-4 py-3 text-left label-eyebrow">Ad Soyad</th>
              <th className="px-4 py-3 text-left label-eyebrow">Uyruk</th>
              <th className="px-4 py-3 text-left label-eyebrow">Doğum</th>
              <th className="px-4 py-3 text-left label-eyebrow">E-posta</th>
              <th className="px-4 py-3 text-right label-eyebrow">Sert.</th>
              <th className="px-4 py-3 text-right label-eyebrow">Saat</th>
              <th className="px-4 py-3 text-right label-eyebrow">İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="px-4 py-8 text-center label-eyebrow">Yükleniyor…</td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-12 text-center label-eyebrow">Henüz öğrenci yok</td></tr>
            )}
            {filtered.map((s) => (
              <tr key={s.id} className={`border-t border-slate-200 hover:bg-slate-50 ${!s.is_active ? "opacity-50" : ""}`} data-testid={`student-row-${s.student_no}`}>
                <td className="px-4 py-3 font-mono-plex font-bold">{s.student_no}</td>
                <td className="px-4 py-3">{s.full_name}{!s.is_active && <span className="ml-2 text-xs text-red-600 font-bold">(PASİF)</span>}</td>
                <td className="px-4 py-3">{s.nationality}</td>
                <td className="px-4 py-3 font-mono-plex">{s.date_of_birth}</td>
                <td className="px-4 py-3 text-xs text-slate-600">{s.email || "—"}</td>
                <td className="px-4 py-3 text-right font-mono-plex">{s.certificates_count}</td>
                <td className="px-4 py-3 text-right font-mono-plex">{s.total_hours}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {s.certificates_count > 0 && (
                    <a
                      href={`${API_BASE}/students/${encodeURIComponent(s.student_no)}/transcript.pdf`}
                      target="_blank" rel="noopener noreferrer"
                      data-testid={`student-transcript-${s.student_no}`}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs text-[#0B2046] hover:bg-slate-100"
                    >
                      <DownloadSimple size={13} /> Transkript
                    </a>
                  )}
                  <button onClick={() => startEdit(s)} className="ml-1 inline-flex items-center gap-1 px-2 py-1 text-xs text-slate-700 hover:bg-slate-100" data-testid={`student-edit-${s.student_no}`}>
                    <Pencil size={13} /> Düzenle
                  </button>
                  {s.is_active ? (
                    <button onClick={() => softDelete(s)} className="ml-1 inline-flex items-center gap-1 px-2 py-1 text-xs text-red-700 hover:bg-red-50" data-testid={`student-delete-${s.student_no}`}>
                      <Trash size={13} /> Pasifleştir
                    </button>
                  ) : (
                    <button onClick={() => restore(s)} className="ml-1 inline-flex items-center gap-1 px-2 py-1 text-xs text-emerald-700 hover:bg-emerald-50" data-testid={`student-restore-${s.student_no}`}>
                      <ArrowCounterClockwise size={13} /> Aktif Yap
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Input({ label, type = "text", required, value, onChange, testid, placeholder, className = "" }) {
  return (
    <div className={className}>
      <label className="label-eyebrow block mb-2">{label}</label>
      <input
        type={type} required={required} value={value} onChange={onChange}
        data-testid={testid} placeholder={placeholder}
        className="w-full px-4 py-3 border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0B2046]"
      />
    </div>
  );
}
